/**
 * App-wide language (Spanish source of truth, optional English). Resolves the
 * active language, exposes the string table, and the <select> change handler.
 * Extracted from App.tsx (Task 201); behavior is unchanged.
 *
 * Precedence: an explicit ?lang= URL param wins (shareable/intentful), else the
 * persisted session's language, else Spanish.
 */
import { useCallback, useState } from "react";
import type { ChangeEvent } from "react";
import { getStrings, resolveLanguage, type Language, type Strings } from "../strings";

export interface UseLanguage {
  language: Language;
  setLanguage: (language: Language) => void;
  strings: Strings;
  handleLanguageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}

export function useLanguage(persistedLanguage: Language | undefined): UseLanguage {
  const [language, setLanguage] = useState<Language>(() => {
    const urlParam =
      typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.search).get("lang");
    if (urlParam !== null) return resolveLanguage(urlParam);
    return persistedLanguage ?? "es";
  });
  const strings = getStrings(language);
  const handleLanguageChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>): void => {
      const next = event.target.value === "en" ? "en" : "es";
      setLanguage(next);
    },
    [],
  );
  return { language, setLanguage, strings, handleLanguageChange };
}
